import { useState, useEffect } from "react";

interface UseGraphNavigationProps {
    graphs: string[] | null;
    currentQuestionNumber: number;
}

interface UseGraphNavigationReturn {
    storedGraphs: string[];
    currentGraphIndex: number;
    hasGraphs: boolean;
    nextGraph: () => void;
    prevGraph: () => void;
}

const useGraphNavigation = ({
    graphs,
    currentQuestionNumber,
}: UseGraphNavigationProps): UseGraphNavigationReturn => {
    const [storedGraphs, setStoredGraphs] = useState<string[]>([]);
    const [currentGraphIndex, setCurrentGraphIndex] = useState(0);

    // Reset graphs when question changes
    useEffect(() => {
        setStoredGraphs([]);
        setCurrentGraphIndex(0);
    }, [currentQuestionNumber]);

    // Process new graphs when they arrive
    useEffect(() => {
        const newGraphs = (graphs ?? [])
            .flatMap((g) => g.split(","))
            .map((str) => str.trim())
            .filter(Boolean);

        if (newGraphs.length > 0) {
            setStoredGraphs(newGraphs);
            setCurrentGraphIndex(0);
        }
    }, [graphs]);

    // Navigation functions
    const nextGraph = () => {
        setCurrentGraphIndex((prev) => (prev + 1) % storedGraphs.length);
    };

    const prevGraph = () => {
        setCurrentGraphIndex(
            (prev) => (prev - 1 + storedGraphs.length) % storedGraphs.length
        );
    };

    return {
        storedGraphs,
        currentGraphIndex,
        hasGraphs: storedGraphs.length > 0,
        nextGraph,
        prevGraph,
    };
};

export default useGraphNavigation;
